import { FiSearch, FiUser, FiCopy, FiMenu, FiPower, FiExternalLink, FiInfo, FiGlobe, FiChevronDown, FiX, FiLoader, FiSend, FiChevronRight, FiPlus } from "react-icons/fi";
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { useWallet } from "../hooks/useWallet";
import { useLanguage } from "../contexts/LanguageContext";
import { toast } from "react-hot-toast";
import UserAvatar from "../images/logo-tcgZone.png";
import StakingTermsPdf from "../file/Staking Participation Consent Risk Disclosure and Acknowledgment v1.pdf";
import { CONTRACT_ADDRESSES, CONTRACT_ABIS } from "../constants";
import { ethers } from "ethers";
import { GetPhonecodes } from "react-country-state-city";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getReferralAddress as getGlobalReferralAddress, setReferralAddress as setGlobalReferralAddress } from "../utils/referralStorage";
import tcgZoneLogo from "../images/logo-tcgZone-3d.png";

const languages = [
	{ code: "en", name: "English" },
	{ code: "zh", name: "简体中文" },
	{ code: "zh-TW", name: "繁體中文" },
	{ code: "ko", name: "한국어" },
	{ code: "vi", name: "Tiếng Việt" },
	{ code: "hi", name: "हिन्दी" },
	{ code: "id", name: "Bahasa Indonesia" },
	{ code: "ta", name: "தமிழ்" },
	{ code: "ja", name: "日本語" },
	{ code: "ru", name: "Русский" },
];

const FALLBACK_COUNTRY_PHONE_CODES = [
	{ code: "+1", label: "US/CA +1" },
	{ code: "+44", label: "UK +44" },
	{ code: "+86", label: "China +86" },
	{ code: "+81", label: "Japan +81" },
	{ code: "+82", label: "Korea +82" },
	{ code: "+65", label: "Singapore +65" },
	{ code: "+60", label: "Malaysia +60" },
	{ code: "+84", label: "Vietnam +84" },
	{ code: "+66", label: "Thailand +66" },
	{ code: "+91", label: "India +91" },
	{ code: "+62", label: "Indonesia +62" },
];

export default function Header({ isMobile, onOpenSidebar }) {
	const { t } = useTranslation();
	const { language, changeLanguage } = useLanguage();
	const [searchQuery, setSearchQuery] = useState("");
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const {
		account,
		isConnecting,
		connectWallet,
		disconnectWallet,
		copyAddress,
		openReown,
		connectById,
		isReownInitializing,
		getSigner: getWalletSigner,
		getProvider,
		isTokenPocketBlocked
	} = useWallet();

	// ✅ menu state
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
	const [isConnectMenuOpen, setIsConnectMenuOpen] = useState(false);
	const [showLoginModal, setShowLoginModal] = useState(false);
	const menuWrapRef = useRef(null);
	const languageMenuRef = useRef(null);
	const connectMenuRef = useRef(null);
	const [UserProfile, setUserProfile] = useState(UserAvatar);
	const [username, setUsername] = useState(t("common.user"));
	const [showKycModal, setShowKycModal] = useState(false);
	const [kycForm, setKycForm] = useState({
		name: "",
		email: "",
		countryCode: "+1",
		phone: "",
		socialMedia: "",
		referral: "",
	});
	const [countryPhoneCodes, setCountryPhoneCodes] = useState(FALLBACK_COUNTRY_PHONE_CODES);
	const [isLoadingCountryPhoneCodes, setIsLoadingCountryPhoneCodes] = useState(false);
	const [bindReferralAddress, setBindReferralAddress] = useState("");
	const [submittingForm, setSubmittingForm] = useState(false);
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [showTermsModal, setShowTermsModal] = useState(false);
	const [referralAddress, setReferralAddress] = useState(null);
	const referralAddressRef = useRef(false);

	const apiBaseUrl = process.env.REACT_APP_API_URL || "https://public-buynode-pokemon.madxapp.com";

	const currentLanguage = languages.find((lang) => lang.code === language) || languages[0];

	// Generate referral URL with account as query parameter
	const getReferralUrl = useCallback(() => {
		if (!account) return "";
		const origin = window.location.origin;
		return `${origin}/referral?=${account}`;
	}, [account]);

	useEffect(() => {
		// Only process referral once on mount to avoid issues in MetaMask browser.
		// Preserve non-referral params such as /gacha?poolId=... for archive pack deep links.
		let referral = searchParams.get('referral');
		let legacyReferralKey = null;
		// Backward compatibility: old links used ?=0x... (no param name); use first query value
		if (!referral) {
			const firstParam = Array.from(searchParams.entries()).find(([, v]) => v && v.startsWith?.("0x"));
			if (firstParam) {
				legacyReferralKey = firstParam[0];
				referral = firstParam[1];
			}
		}
		// If not found in searchParams, try to get from global storage
		if (!referral) {
			referral = getGlobalReferralAddress();
		}

		if (referral) {
			// Set referral address in both local state and global storage
			setReferralAddress(referral);
			setGlobalReferralAddress(referral); // Store in global storage
			// Clear only referral params after a short delay to ensure state update completes.
			// This prevents wiping route-specific params such as poolId.
			const clearParamsTimer = setTimeout(() => {
				const nextParams = new URLSearchParams(searchParams);
				['referral', 'referrer', 'refeer', 'reffer'].forEach((key) => nextParams.delete(key));
				if (legacyReferralKey !== null) nextParams.delete(legacyReferralKey);
				setSearchParams(nextParams, { replace: true });
			}, 100);
			return () => clearTimeout(clearParamsTimer);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Empty dependency array - only run once on mount

	// Load country phone codes dynamically (once). Uses GetPhonecodes (phone_code field). Falls back to hardcoded list.
	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			setIsLoadingCountryPhoneCodes(true);
			try {
				const list = await GetPhonecodes();
				const codes = (list || [])
					.map((c) => {
						const raw = c?.phone_code;
						const normalized = typeof raw === "string" ? raw.trim() : raw;
						if (!normalized) return null;
						const code = normalized.startsWith("+") ? normalized : `+${normalized}`;
						const name = c?.name || code;
						return { code, label: `${name} ${code}` };
					})
					.filter(Boolean);

				const seen = new Set();
				const unique = [];
				for (const item of codes) {
					if (seen.has(item.code)) continue;
					seen.add(item.code);
					unique.push(item);
				}
				unique.sort((a, b) => a.label.localeCompare(b.label));

				if (!cancelled && unique.length) {
					setCountryPhoneCodes(unique);
				}
			} catch (e) {
				console.error("Error loading country phone codes:", e);
				// keep fallback
			} finally {
				if (!cancelled) setIsLoadingCountryPhoneCodes(false);
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, []);

	// Ensure selected countryCode exists in current options
	useEffect(() => {
		if (!Array.isArray(countryPhoneCodes) || !countryPhoneCodes.length) return;
		const exists = countryPhoneCodes.some((c) => c.code === kycForm.countryCode);
		if (!exists) {
			setKycForm((p) => ({ ...p, countryCode: countryPhoneCodes[0].code }));
		}
	}, [countryPhoneCodes, kycForm.countryCode]);

	// ✅ click outside to close menu
	useEffect(() => {
		if (!isMenuOpen) return;

		const onDown = (e) => {
			if (!menuWrapRef.current) return;
			if (!menuWrapRef.current.contains(e.target)) setIsMenuOpen(false);
		};

		const onKey = (e) => {
			if (e.key === "Escape") setIsMenuOpen(false);
		};

		document.addEventListener("mousedown", onDown);
		document.addEventListener("touchstart", onDown, { passive: true });
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("touchstart", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [isMenuOpen]);

	// ✅ click outside to close language menu
	useEffect(() => {
		if (!isLanguageMenuOpen) return;

		const onDown = (e) => {
			if (!languageMenuRef.current) return;
			if (!languageMenuRef.current.contains(e.target)) setIsLanguageMenuOpen(false);
		};

		const onKey = (e) => {
			if (e.key === "Escape") setIsLanguageMenuOpen(false);
		};

		document.addEventListener("mousedown", onDown);
		document.addEventListener("touchstart", onDown, { passive: true });
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("touchstart", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [isLanguageMenuOpen]);

	// ✅ click outside to close connect menu
	useEffect(() => {
		if (!isConnectMenuOpen) return;
		const onDown = (e) => {
			if (connectMenuRef.current && !connectMenuRef.current.contains(e.target)) setShowLoginModal(false);
		};
		const onKey = (e) => {
			if (e.key === "Escape") setShowLoginModal(false);
		};
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [isConnectMenuOpen]);

	useEffect(() => {
		// useEffect cannot be async directly, so we define an async function inside
		const fetchUserDetails = async () => {
			if (!account) {
				setUsername("User");
				setUserProfile(UserAvatar);
			} else {
				try {
					const response = await fetch(`${apiBaseUrl}/publicApi/getUserDetails`, {
						method: 'POST',
						headers: {
							Accept: 'application/json'
						},
						body: JSON.stringify({ address: account }, null, 0),
					});

					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}

					const responseText = await response.json();

					const username_response = responseText.data.user_name || t("common.user");
					const profileImage_response = responseText.data.user_img || UserAvatar;

					setUsername(username_response);
					setUserProfile(profileImage_response);

				} catch (error) {
					console.error("Error fetching user details:", error);
					toast.error("Failed to fetch user details");
				}
			}
		};

		fetchUserDetails();
	}, [account]); // Only account is a dependency since apiBaseUrl is a constant

	const checkFillInformation = useCallback(async () => {
		if (!account) return;
		try {
			const response = await fetch(`${apiBaseUrl}/publicApi/checkFillInformation`, {
				method: 'POST',
				headers: { Accept: 'application/json' },
				body: JSON.stringify({ wallet_address: account }, null, 0),
			});

			const responseText = await response.json();

			if (!response.ok) {
				throw new Error(responseText.message || responseText.error || `HTTP error! status: ${response.status}`);
			}

			if (!responseText.success) {
				setShowKycModal(true);
			}

		} catch (error) {
			console.error("Error checking fill information:", error);
			toast.error(t("header.kyc.failedToCheckFillInformation"));
		}
	}, [account]);

	// useEffect(() => {
	// 	checkFillInformation();
	// }, [checkFillInformation]);

	const submitKyInformation = useCallback(async () => {
		if (!account) return;

		if (kycForm.name === "" || kycForm.email === "" || kycForm.countryCode === "" || kycForm.phone === "" || kycForm.socialMedia === "") {
			toast.error(t("header.kyc.requiredFields"));
			return;
		}

		if (!bindReferralAddress && kycForm.referral === "") {
			toast.error(t("header.kyc.requiredFields"));
			return;
		}

		if (!termsAccepted) {
			toast.error(t("header.kyc.acceptTermsError"));
			return;
		}

		// Email format: basic valid email pattern
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(kycForm.email.trim())) {
			toast.error(t("header.kyc.invalidEmail"));
			return;
		}

		// Phone: digits only (after stripping spaces/dashes), 4–15 digits for national number
		const phoneDigits = (kycForm.phone || "").replace(/\D/g, "");
		if (phoneDigits.length < 4 || phoneDigits.length > 15) {
			toast.error(t("header.kyc.invalidPhone"));
			return;
		}

		try {
			setSubmittingForm(true);
			toast.loading(t("header.kyc.submittingInformation"), { id: "save-information-tx" });

			const response = await fetch(`${apiBaseUrl}/publicApi/saveInformation`, {
				method: 'POST',
				headers: { Accept: 'application/json' },
				body: JSON.stringify({
					walletAddress: account,
					userPublicName: kycForm.name,
					userEmail: kycForm.email,
					mobileCountryCode: kycForm.countryCode,
					mobileNo: kycForm.phone,
					sosialId: kycForm.socialMedia,
				}, null, 0),
			});

			const responseText = await response.json();

			if (!response.ok) {
				throw new Error(responseText.message || responseText.error || `HTTP error! status: ${response.status}`);
			}

			if (!bindReferralAddress && kycForm.referral !== "") {
				const signer = await getWalletSigner();

				const ReferralsContract = new ethers.Contract(
					CONTRACT_ADDRESSES.REFERRALS,
					CONTRACT_ABIS.REFERRALS,
					signer
				);

				const bindReferralTx = await ReferralsContract.bindReferral(kycForm.referral, account);

				toast.loading(t("common.waitingConfirmation"), { id: "bind-referral-tx" });
				await bindReferralTx.wait();
				toast.dismiss("bind-referral-tx");
			}

			toast.dismiss("save-information-tx");
			toast.success(t("header.kyc.successSubmitKYC"));
			setShowKycModal(false);
			setKycForm({ name: "", email: "", countryCode: "+1", phone: "", socialMedia: "", referral: "" });
			setTermsAccepted(false);
		} catch (error) {
			console.error("Error submitting KY information:", error);
			toast.error(t("header.kyc.failedToSubmitKYC"));
		} finally {
			setSubmittingForm(false);
		}
	}, [account, kycForm, bindReferralAddress, getWalletSigner, termsAccepted]);

	const getReferralAddress = async () => {
		try {
			const provider = await getProvider();
			const ReferralsContract = new ethers.Contract(
				CONTRACT_ADDRESSES.REFERRALS,
				CONTRACT_ABIS.REFERRALS,
				provider
			);
			const referralAddress = await ReferralsContract.bindReferrals(account);

			setBindReferralAddress(referralAddress);
		} catch (error) {
			console.error("Error getting bind referral address:", error);
			toast.error(t("buyPack.failedGetReferral"));
		}
	};

	useEffect(() => {
		if (!account) {
			return;
		}
		if (referralAddressRef.current) {
			return;
		}
		referralAddressRef.current = true;
		getReferralAddress().finally(() => {
			referralAddressRef.current = false;
		});
	}, [account]);


	// Close partner recruit pop-out on Escape
	// useEffect(() => {
	// 	if (!showKycModal) return;
	// 	const onKey = (e) => { if (e.key === "Escape") setShowKycModal(false); };
	// 	document.addEventListener("keydown", onKey);
	// 	return () => document.removeEventListener("keydown", onKey);
	// }, [showKycModal]);

	return (
		<header
			className="sticky-header"
			style={{
				position: "relative",
				padding: isMobile ? "12px 12px" : "16px 20px",
			}}
		>

			{/* ✅ Mobile Logo（只在 mobile 显示，替换原来的 FiMenu） */}
			{isMobile && (
				<div
					onClick={() => navigate('/main')}
					style={{
						display: "flex",
						alignItems: "center",
						cursor: "pointer",
					}}
				>
					<img
						src={UserAvatar}
						alt="TCG Zone Logo"
						style={{
							height: "32px",
							width: "auto",
							objectFit: "contain"
						}}
					/>
				</div>
			)}

			{/* Right Side - Offers Button and Profile */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: isMobile ? "8px" : "12px",
					marginLeft: "auto",
				}}
			>

				{/* Swap Button - navigates to Profile Swap tab (maintenance modal is shown there) */}
				<button
					onClick={() => navigate('/profile?tab=swap')}
					className="rounded-xl border border-white/12 bg-gray-950/40 text-white font-semibold cursor-pointer transition-all hover:bg-white/10"
					style={{
						padding: isMobile ? "8px 12px" : "10px 20px",
						fontSize: isMobile ? "13px" : "14px",
						whiteSpace: "nowrap",
						wordBreak: "keep-all",
						lineHeight: "1.5",
					}}
				>
					{t("header.swap")}
				</button>

				{/* Language Selector */}
				<div ref={languageMenuRef} style={{ position: "relative" }}>
					<button
						type="button"
						onClick={() => setIsLanguageMenuOpen((v) => !v)}
						style={{
							display: "flex",
							alignItems: "center",
							gap: isMobile ? "4px" : "8px",
							padding: isMobile ? "6px 8px" : "8px 12px",
							borderRadius: "12px",
							border: "1px solid rgba(255,255,255,0.12)",
							background: "rgba(0,0,0,0.4)",
							color: "#fff",
							fontSize: isMobile ? "13px" : "14px",
							fontWeight: 500,
							cursor: "pointer",
							transition: "transform, opacity, background-color, border-color, color 0.2s ease",
						}}
						onMouseEnter={(e) => {
							e.currentTarget.style.background = "rgba(0,0,0,0.6)";
							e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.background = "rgba(0,0,0,0.4)";
							e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
						}}
					>
						<FiGlobe size={18} />
						<span style={{
							whiteSpace: "nowrap",
							wordBreak: "keep-all",
							fontSize: isMobile ? "13px" : "14px",
						}}>{currentLanguage.name}</span>
						<FiChevronDown size={14} style={{ opacity: 0.7 }} />
					</button>

					{/* Language Dropdown Menu */}
					{isLanguageMenuOpen && (
						<div
							style={{
								position: "absolute",
								top: "calc(100% + 10px)",
								right: 0,
								width: 200,
								borderRadius: "12px",
								border: "1px solid rgba(255,255,255,0.12)",
								background: "#fff",
								boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
								overflow: "hidden",
								zIndex: 43,
								padding: "8px 0",
							}}
						>
							{languages.map((lang) => (
								<button
									key={lang.code}
									onClick={() => {
										changeLanguage(lang.code);
										setIsLanguageMenuOpen(false);
									}}
									style={{
										width: "100%",
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										padding: "12px 16px",
										background: language === lang.code ? "rgba(59, 130, 246, 0.1)" : "transparent",
										color: language === lang.code ? "#3b82f6" : "#1f2937",
										fontSize: "14px",
										fontWeight: language === lang.code ? 600 : 400,
										cursor: "pointer",
										border: "none",
										transition: "transform, opacity, background-color, border-color, color 0.15s ease",
										textAlign: "left",
									}}
									onMouseEnter={(e) => {
										if (language !== lang.code) {
											e.currentTarget.style.background = "rgba(0,0,0,0.05)";
										}
									}}
									onMouseLeave={(e) => {
										if (language !== lang.code) {
											e.currentTarget.style.background = "transparent";
										}
									}}
								>
									<span>{lang.name}</span>
									{language === lang.code && (
										<div
											style={{
												width: 8,
												height: 8,
												borderRadius: "50%",
												background: "#3b82f6",
											}}
										/>
									)}
								</button>
							))}
						</div>
					)}
				</div>

				{account ? (
					<div ref={menuWrapRef} style={{ position: "relative" }}>
						{/* ✅ Clickable pill */}
						<button
							type="button"
							onClick={() => setIsMenuOpen((v) => !v)}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "10px",
								color: "#fff",
								border: "1px solid #333",
								borderRadius: "15px",
								padding: "6px 10px",
								background: "rgba(0,0,0,0.35)",
								cursor: "pointer",
							}}
							aria-haspopup="menu"
							aria-expanded={isMenuOpen}
						>
							<div style={{ width: 32, height: 32, borderRadius: 999, overflow: "hidden", background: "#222" }}>
								<img src={UserProfile} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
							</div>

							<span style={{ fontSize: 12, fontWeight: 600 }}>
								{account.slice(0, 6)}...{account.slice(-4)}
							</span>
							<FiCopy
								onClick={(e) => {
									e.stopPropagation();
									copyAddress();
								}}
								title={t("header.copyAddress")}
								style={{ opacity: 0.8 }}
							/>

							<span
								onClick={(e) => {
									e.stopPropagation();
									disconnectWallet();
									setIsMenuOpen(false);
								}}
								title={t("header.disconnect")}
								style={{
									display: "inline-flex",
									width: 22,
									height: 22,
									alignItems: "center",
									justifyContent: "center",
									borderRadius: 8,
									color: "rgba(255,255,255,0.8)",
								}}
							>
								×
							</span>
						</button>

						{/* ✅ Dropdown Menu */}
						{isMenuOpen && (
							<div
								style={{
									position: "absolute",
									top: "calc(100% + 10px)",
									right: 0,
									width: isMobile ? "92vw" : 360,
									maxWidth: 420,
									borderRadius: 18,
									border: "1px solid rgba(255,255,255,0.12)",
									background: "rgba(18,18,18,0.92)",
									backdropFilter: "blur(10px)",
									boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
									overflow: "hidden",
									zIndex: 42,
								}}
								role="menu"
							>
								{/* Header area */}
								<div style={{ padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
									<div style={{ display: "flex", gap: 12, alignItems: "center" }}>
										<div style={{ width: 42, height: 42, borderRadius: 999, overflow: "hidden", background: "#222" }}>
											<img src={UserProfile} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
										</div>
										<div>
											<div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>{t("common.user")}</div>
											<div style={{ fontWeight: 600, fontSize: 18, color: "#fff", lineHeight: 1.1 }}>
												{username}
											</div>
										</div>
									</div>

									<button
										onClick={() => {
											disconnectWallet();
											setIsMenuOpen(false);
										}}
										style={{ border: "none", background: "transparent", color: "rgba(255,255,255,0.8)", cursor: "pointer", fontSize: 18 }}
										title="Disconnect"
									>
										<FiPower />
									</button>
								</div>

								{/* Address blocks */}
								<div style={{ padding: "0 16px 14px" }}>
									<div style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, marginBottom: 6 }}>{t("header.referralAddress")}</div>
									<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
										<div style={{ color: "#fff", fontSize: 13, fontWeight: 600, wordBreak: "break-all", flex: 1 }}>
											{getReferralUrl()}
										</div>
										<button
											onClick={() => {
												const referralUrl = getReferralUrl();
												if (referralUrl) {
													navigator.clipboard.writeText(referralUrl);
													toast.success(t("header.referralUrlCopied"));
												}
											}}
											style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", padding: "4px", display: "flex", opacity: 0.8 }}
										>
											<FiCopy size={16} />
										</button>
									</div>
								</div>
							</div>
						)}
					</div>
				) : !isTokenPocketBlocked ? (
					<>
						{/* ✅ This button triggers the modal if it's not open */}
						<button
							onClick={() => openReown()}
							className="px-6 py-2 bg-blue-600 text-white rounded-full font-bold"
							style={{
								padding: isMobile ? "8px 12px" : "10px 20px",
								borderRadius: 12,
								background: "linear-gradient(135deg, rgba(151, 112, 221, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)",
								border: "none",
								color: "#fff",
								fontSize: isMobile ? "13px" : "14px",
								fontWeight: 600,
								cursor: isConnecting || isReownInitializing ? "not-allowed" : "pointer",
								transition: "transform, opacity, background-color, border-color, color 0.2s ease",
								opacity: isConnecting || isReownInitializing ? 0.6 : 1,
								display: "flex",
								alignItems: "center",
								gap: 6,
							}}
						>
							{t('header.login')}
						</button>
					</>
				) : null}
			</div>

			{/* Partner recruit pop-out only – open with setShowPartnerRecruitModal(true) */}
			{showKycModal && (
				<div
					style={{
						position: "fixed",
						inset: 0,
						background: "rgba(0,0,0,0.75)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 47,
						padding: 16,
					}}
				// onClick={() => setShowKycModal(false)}
				>
					<div
						style={{
							width: "100%",
							maxWidth: 420,
							maxHeight: "90vh",
							borderRadius: 18,
							background: "rgba(18,18,18,0.98)",
							border: "1px solid rgba(255,255,255,0.12)",
							boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 16px 12px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
							<div style={{ color: "#fff", fontWeight: 600, fontSize: 16 }}>{t("header.kyc.title")}</div>
							{/* <button
								type="button"
								onClick={() => setShowKycModal(false)}
								style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.75)", cursor: "pointer", padding: 6 }}
								aria-label={t("common.close")}
							>
								<FiX size={20} />
							</button> */}
						</div>
						<div style={{ padding: 16, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
							<div>
								<div style={{ marginBottom: 6, color: "rgba(255,255,255,0.95)", fontSize: 13, fontWeight: 600 }}>{t("header.kyc.name")} <span style={{ color: "rgba(255,90,90,0.95)" }}>*</span></div>
								<input
									value={kycForm.name}
									onChange={(e) => setKycForm((p) => ({ ...p, name: e.target.value }))}
									placeholder={t("header.kyc.namePlaceholder")}
									style={{ width: "100%", padding: "12px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(0,0,0,0.25)", color: "#fff", outline: "none", fontSize: 14, boxSizing: "border-box" }}
								/>
							</div>
							<div>
								<div style={{ marginBottom: 6, color: "rgba(255,255,255,0.95)", fontSize: 13, fontWeight: 600 }}>{t("header.kyc.email")} <span style={{ color: "rgba(255,90,90,0.95)" }}>*</span></div>
								<input
									type="email"
									value={kycForm.email}
									onChange={(e) => setKycForm((p) => ({ ...p, email: e.target.value }))}
									placeholder={t("header.kyc.emailPlaceholder")}
									style={{ width: "100%", padding: "12px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(0,0,0,0.25)", color: "#fff", outline: "none", fontSize: 14, boxSizing: "border-box" }}
								/>
							</div>
							<div>
								<div style={{ marginBottom: 6, color: "rgba(255,255,255,0.95)", fontSize: 13, fontWeight: 600 }}>{t("header.kyc.countryCode")}</div>
								<select
									value={kycForm.countryCode}
									onChange={(e) => setKycForm((p) => ({ ...p, countryCode: e.target.value }))}
									style={{ width: "100%", padding: "12px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(0,0,0,0.25)", color: "#fff", outline: "none", fontSize: 14, boxSizing: "border-box", cursor: "pointer" }}
								>
									{isLoadingCountryPhoneCodes && (
										<option value={kycForm.countryCode} style={{ background: "#1a1a1a", color: "#fff" }}>
											{kycForm.countryCode}
										</option>
									)}
									{countryPhoneCodes.map((opt) => (
										<option key={opt.code} value={opt.code} style={{ background: "#1a1a1a", color: "#fff" }}>
											{opt.label}
										</option>
									))}
								</select>
							</div>
							<div>
								<div style={{ marginBottom: 6, color: "rgba(255,255,255,0.95)", fontSize: 13, fontWeight: 600 }}>{t("header.kyc.phone")} <span style={{ color: "rgba(255,90,90,0.95)" }}>*</span></div>
								<input
									type="tel"
									value={kycForm.phone}
									onChange={(e) => setKycForm((p) => ({ ...p, phone: e.target.value }))}
									placeholder={t("header.kyc.phonePlaceholder")}
									style={{ width: "100%", padding: "12px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(0,0,0,0.25)", color: "#fff", outline: "none", fontSize: 14, boxSizing: "border-box" }}
								/>
							</div>
							<div>
								<div style={{ marginBottom: 6, color: "rgba(255,255,255,0.95)", fontSize: 13, fontWeight: 600 }}>{t("header.kyc.socialMedia")} <span style={{ color: "rgba(255,90,90,0.95)" }}>*</span></div>
								<input
									value={kycForm.socialMedia}
									onChange={(e) => setKycForm((p) => ({ ...p, socialMedia: e.target.value }))}
									placeholder={t("header.kyc.socialMediaPlaceholder")}
									style={{ width: "100%", padding: "12px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(0,0,0,0.25)", color: "#fff", outline: "none", fontSize: 14, boxSizing: "border-box" }}
								/>
							</div>
							{!bindReferralAddress && (
								<div>
									<div style={{ marginBottom: 6, color: "rgba(255,255,255,0.95)", fontSize: 13, fontWeight: 600 }}>{t("header.kyc.referral")} <span style={{ color: "rgba(255,90,90,0.95)" }}>*</span></div>
									<input
										value={referralAddress || kycForm.referral}
										onChange={(e) => setKycForm((p) => ({ ...p, referral: e.target.value }))}
										placeholder={t("header.kyc.referralPlaceholder")}
										style={{ width: "100%", padding: "12px 12px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(0,0,0,0.25)", color: "#fff", outline: "none", fontSize: 14, boxSizing: "border-box" }}
									/>
								</div>
							)}
						</div>

						<div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, borderTop: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
							<label style={{ display: "flex", alignItems: "center", gap: 10, color: "rgba(255,255,255,0.85)", fontSize: 13, userSelect: "none", cursor: "pointer" }}>
								<input
									type="checkbox"
									checked={termsAccepted}
									onChange={(e) => setTermsAccepted(e.target.checked)}
									style={{ width: 16, height: 16, cursor: "pointer" }}
								/>
								<span>
									{t("header.kyc.acceptTermsPrefix")}{" "}
									<button
										type="button"
										onClick={() => setShowTermsModal(true)}
										style={{
											background: "transparent",
											border: "none",
											padding: 0,
											margin: 0,
											color: "rgba(167, 139, 250, 1)",
											fontWeight: 600,
											textDecoration: "underline",
											cursor: "pointer",
										}}
									>
										{t("header.kyc.termsAndConditions")}
									</button>
								</span>
							</label>

							<div style={{ display: "flex", gap: 10 }}>
								{/* <button
								type="button"
								onClick={() => setShowKycModal(false)}
								style={{ flex: 1, padding: "12px 14px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.12)", background: "transparent", color: "rgba(255,255,255,0.85)", fontWeight: 600, cursor: "pointer" }}
							>
								{t("common.cancel")}
							</button> */}
								<button
									type="button"
									onClick={() => { submitKyInformation(); }}
									disabled={submittingForm || !termsAccepted}
									style={{ flex: 1, padding: "12px 14px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, rgba(151, 112, 221, 0.9) 0%, rgba(139, 92, 246, 0.9) 100%)", color: "#fff", fontWeight: 600, cursor: submittingForm || !termsAccepted ? "not-allowed" : "pointer", opacity: submittingForm || !termsAccepted ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
								>
									{submittingForm ? <FiLoader size={16} style={{ animation: "spin 1s linear infinite" }} /> : <FiSend size={16} />}
									{t("header.kyc.submit")}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{showTermsModal && (
				<div
					onClick={() => setShowTermsModal(false)}
					style={{
						position: "fixed",
						inset: 0,
						background: "rgba(0,0,0,0.8)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 48,
						padding: 16,
					}}
				>
					<div
						onClick={(e) => e.stopPropagation()}
						style={{
							width: "100%",
							maxWidth: 900,
							height: "90vh",
							borderRadius: 16,
							background: "rgba(18,18,18,0.98)",
							border: "1px solid rgba(255,255,255,0.12)",
							boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
							<div style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>{t("header.kyc.termsAndConditions")}</div>
							<button
								type="button"
								onClick={() => setShowTermsModal(false)}
								style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.75)", cursor: "pointer", padding: 6 }}
								aria-label={t("common.close")}
							>
								<FiX size={20} />
							</button>
						</div>
						<div style={{ flex: 1, background: "#0b0b0b" }}>
							<iframe
								title="Terms and Conditions PDF"
								src={StakingTermsPdf}
								style={{ width: "100%", height: "100%", border: "none" }}
							/>
						</div>
					</div>
				</div>
			)}

		</header>
	);
}
